import React, { useState } from 'react';
import {
  motion,
  useReducedMotion,
  useTransform,
  useViewportScroll,
} from 'framer-motion';
import posterDesktop from '../../assets/images/hero-poster-desktop.jpg';
import posterMobile from '../../assets/images/hero-poster-mobile.jpg';
import posterDesktopWebp from '../../assets/images/hero-poster-desktop.webp';
import posterMobileWebp from '../../assets/images/hero-poster-mobile.webp';
import desktopAv1 from '../../assets/videos/hero-desktop.av1.mp4';
import desktopHevc from '../../assets/videos/hero-desktop.hevc.mp4';
import mobileAv1 from '../../assets/videos/hero-mobile.av1.mp4';
import mobileHevc from '../../assets/videos/hero-mobile.hevc.mp4';
import { useIsIdle, useIsLowDataConnection } from '../../../../utils/hooks';

const MOBILE_QUERY = '(max-width: 767px)';
const BACKGROUND_PARALLAX_RATE = 0.35;

// Browsers play the first source they support: AV1 (Chrome, Firefox, Edge), then HEVC
// (Safari). Anything older keeps the poster, as on slow connections.
const HERO_VIDEO_SOURCES = {
  mobile: [
    { src: mobileAv1, type: 'video/mp4; codecs="av01.0.05M.08"' },
    { src: mobileHevc, type: 'video/mp4; codecs="hvc1.1.6.L93.90"' },
  ],
  desktop: [
    { src: desktopAv1, type: 'video/mp4; codecs="av01.0.08M.08"' },
    { src: desktopHevc, type: 'video/mp4; codecs="hvc1.1.6.L120.90"' },
  ],
};

function HeroBackground() {
  const reducedMotion = useReducedMotion();
  const { scrollY } = useViewportScroll();
  const backgroundY = useTransform(
    scrollY,
    (y) => y * BACKGROUND_PARALLAX_RATE
  );
  const isIdle = useIsIdle();
  const isLowDataConnection = useIsLowDataConnection();
  const [videoPlaying, setVideoPlaying] = useState(false);

  // The poster is the LCP image, so the video only starts loading once the page is idle.
  const showVideo = isIdle && !reducedMotion && !isLowDataConnection;
  const videoSources = window.matchMedia(MOBILE_QUERY).matches
    ? HERO_VIDEO_SOURCES.mobile
    : HERO_VIDEO_SOURCES.desktop;

  return (
    <motion.div
      className='hh-hero__background'
      style={reducedMotion ? undefined : { y: backgroundY }}
      aria-hidden='true'
    >
      {/*
        A <picture> rather than the video's poster attribute: it needs mobile and
        desktop crops in WebP and JPEG, and must show when no video renders at all
        (slow connections, reduced motion, unsupported codecs).
      */}
      <picture>
        <source
          media={MOBILE_QUERY}
          type='image/webp'
          srcSet={posterMobileWebp}
        />
        <source media={MOBILE_QUERY} srcSet={posterMobile} />
        <source type='image/webp' srcSet={posterDesktopWebp} />
        <img
          className='hh-hero__media hh-hero__poster'
          src={posterDesktop}
          alt='Aerial view of fishing lakes surrounded by trees and fields at sunset'
          fetchpriority='high'
        />
      </picture>
      {showVideo && (
        <video
          className={`hh-hero__media hh-hero__video ${
            videoPlaying ? 'hh-hero__video--playing' : ''
          }`}
          autoPlay
          muted
          loop
          playsInline
          onPlaying={() => setVideoPlaying(true)}
        >
          {videoSources.map(({ src, type }) => (
            <source key={src} src={src} type={type} />
          ))}
        </video>
      )}
    </motion.div>
  );
}

export default HeroBackground;
